/**
 * actions.js — Akili Brain: Action Execution & Message Enrichment
 * Handles [ACTION:…|{…}] markers embedded in AI responses, portal navigation
 * calls, source panels, and the grounded badge.
 */

import { escapeHtml, escapeAttr } from './ui-renderers';

// M-13 fix: always construct a fresh regex per invocation to avoid lastIndex drift
const ACTION_RE_SRC = '\\[ACTION:([a-z_]+)\\|(\\{[^}]*\\})\\]';

// ── Portal action dispatch ────────────────────────────────────────────────────

export function executeAction(actionName: string, params: any) {
  console.info('[AkiliBrain] Executing action:', actionName, params);
  switch (actionName) {
    case 'prefill_update':
      if (typeof (window as any).openSendUpdateModal === 'function') {
        (window as any).openSendUpdateModal(params.matter_id, params.draft);
      }
      break;
    case 'open_section':
      if (typeof (window as any).showSection === 'function') {
        (window as any).showSection(params.section);
      }
      break;
    case 'create_task':
      if (typeof (window as any).openCreateTaskModal === 'function') {
        (window as any).openCreateTaskModal(params.title, params.due_date);
      }
      break;
    default:
      console.warn('[AkiliBrain] Unknown action:', actionName);
  }
}

/**
 * Strips [ACTION:…] markers from rawText, dispatches each found action,
 * and returns the cleaned text for display.
 */
export function handleActionMarkers(rawText: string): string {
  const re = new RegExp(ACTION_RE_SRC, 'g');
  let match;
  while ((match = re.exec(rawText)) !== null) {
    try {
      executeAction(match[1], JSON.parse(match[2]));
    } catch (e) {
      console.warn('[AkiliBrain] Invalid action params:', match[2], e);
    }
  }
  return rawText.replace(new RegExp(ACTION_RE_SRC, 'g'), '');
}

// ── Sources panel ─────────────────────────────────────────────────────────────

export function renderSourcesPanel(sources: any[], bubble: Element) {
  if (!sources || !sources.length || !bubble) return;
  const wrap = bubble.querySelector('.ai-msg-body-wrap');
  if (!wrap) return;
  const existing = wrap.querySelector('.ai-sources-panel');
  if (existing) existing.remove();

  const panel = document.createElement('div');
  panel.className = 'ai-sources-panel';

  const header = '<button class="ai-sources-toggle" aria-expanded="false">' +
    '<svg viewBox="0 0 16 16" fill="currentColor" style="width:12px;height:12px"><path d="M2 3h12v1.5H2zm0 4h12v1.5H2zm0 4h8v1.5H2z"/></svg> ' +
    sources.length + ' source' + (sources.length > 1 ? 's' : '') +
    '<svg class="ai-sources-chevron" viewBox="0 0 16 16" fill="currentColor" style="width:10px;height:10px;margin-left:auto"><path d="M4.5 6L8 9.5 11.5 6"/></svg>' +
    '</button>';

  let list = '<div class="ai-sources-list" style="display:none">';
  for (const s of sources) {
    list += '<a class="ai-source-item" href="' + escapeAttr(s.url || '#') + '" target="_blank" rel="noopener">';
    list += '<span class="ai-source-title">' + escapeHtml(s.title || 'Untitled') + '</span>';
    if (s.citation) list += '<span class="ai-source-cite">'    + escapeHtml(s.citation) + '</span>';
    if (s.excerpt)  list += '<span class="ai-source-excerpt">' + escapeHtml(s.excerpt.slice(0, 120)) + '\u2026</span>';
    list += '</a>';
  }
  list += '</div>';

  panel.innerHTML = header + list;

  const toggleBtn = panel.querySelector('.ai-sources-toggle');
  if (toggleBtn) {
    toggleBtn.addEventListener('click', function (this: HTMLButtonElement) {
      const listEl = panel.querySelector('.ai-sources-list') as HTMLElement | null;
      if (!listEl) return;
      const expanded = listEl.style.display !== 'none';
      listEl.style.display = expanded ? 'none' : '';
      this.setAttribute('aria-expanded', String(!expanded));
      panel.classList.toggle('expanded', !expanded);
    });
  }

  wrap.appendChild(panel);
}

// ── Grounded badge ────────────────────────────────────────────────────────────

export function renderGroundedBadge(bubble: Element) {
  if (!bubble) return;
  const body = bubble.querySelector('.ai-msg-body');
  if (!body) return;
  if (body.querySelector('.ai-grounded-badge')) return;
  const badge = document.createElement('span');
  badge.className = 'ai-grounded-badge';
  badge.innerHTML = '<svg viewBox="0 0 16 16" fill="currentColor" style="width:11px;height:11px"><path d="M8 1l2.35 4.76 5.25.77-3.8 3.7.9 5.24L8 13.27l-4.7 2.47.9-5.24-3.8-3.7 5.25-.77z"/></svg> Grounded';
  body.insertBefore(badge, body.firstChild);
}